import React, { useState, useEffect, useMemo } from 'react';
import { 
  CheckCircle2, 
  Clock, 
  XCircle, 
  TrendingUp, 
  Layers, 
  BarChart3
} from 'lucide-react';
import { ProjectSummaryStats, StepTest, getStepStartTimeMs } from '../types';

interface DashboardProps {
  stats: ProjectSummaryStats;
  selectedController: string;
  onSelectController: (controller: string) => void;
  steps?: StepTest[];
  now?: number;
  targetMovesPerMv?: number;
  onOpenParametersModal?: () => void;
  onSelectActiveStep?: (step: StepTest) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  stats,
  selectedController,
  onSelectController,
  steps = [] as StepTest[],
  now: externalNow,
  targetMovesPerMv = 20,
  onOpenParametersModal,
  onSelectActiveStep,
}) => {
  const [internalNow, setInternalNow] = useState<number>(() => Date.now());
  const now = externalNow ?? internalNow;
  const subcontrollers = Object.keys(stats.controllerStats || {});
  const projectName = stats.projectName || stats.campaignName || 'APC Step Test Project';
  const progressPct = stats.projectProgressPct ?? stats.campaignProgressPct ?? 0;

  // Check if any active step is in progress
  const hasActiveSteps = useMemo(() => {
    return (steps || []).some((s) => s.status === 'IN_PROGRESS' && !s.completedAt);
  }, [steps]);

  // Tick every second only when an active step exists and externalNow is not provided
  useEffect(() => {
    if (externalNow !== undefined || !hasActiveSteps) return;
    const interval = setInterval(() => {
      setInternalNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [externalNow, hasActiveSteps]);

  // Find all active steps in progress, sorted with soonest to finish first
  const allActiveSteps = useMemo(() => {
    if (!steps || steps.length === 0) return [];
    const active = steps.filter((s) => s.status === 'IN_PROGRESS' && !s.completedAt);
    if (active.length === 0) return [];

    const list = active.map((s) => {
      const startMs = getStepStartTimeMs(s);
      const durationMinutes = s.holdDuration || 60;
      const endMs = startMs + durationMinutes * 60 * 1000;
      const remainingMs = endMs - now;
      return {
        step: s,
        remainingMs,
      };
    });

    list.sort((a, b) => a.remainingMs - b.remainingMs);
    return list;
  }, [steps, now]);

  const formatTimeRemaining = (remainingMs: number) => {
    if (remainingMs <= 0) {
      return 'Ready';
    }
    const totalSecs = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m left`;
    }
    return `${mins}m ${secs.toString().padStart(2, '0')}s left`;
  };

  // Dynamically scale columns so subcontrollers evenly fill and distribute across the full width
  const getSubcontrollerGridClass = (count: number) => {
    switch (count) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 3:
        return 'grid-cols-1 sm:grid-cols-3';
      case 4:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
      case 5:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
      case 6:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6';
      default:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6';
    }
  };

  return (
    <div className="space-y-4">
      {/* Project Summary Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                {projectName}
              </h1>
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                Plant Step-Testing
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Target: <span className="font-semibold text-slate-700">{targetMovesPerMv} valid moves</span> per variable for controller model tuning.
              </p>
              {onOpenParametersModal && (
                <button
                  type="button"
                  onClick={onOpenParametersModal}
                  className="mt-1 text-[11px] font-semibold text-sky-600 hover:text-sky-800 hover:underline cursor-pointer transition-colors"
                  title="Configure target moves per MV"
                >
                  Configure Target
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 shrink-0 self-end sm:self-auto">
            {/* All variables currently being stepped */}
            {allActiveSteps.length > 0 && (
              <div className="flex items-center gap-2 pr-3 sm:pr-4 border-r border-slate-200 flex-wrap max-w-full sm:max-w-md lg:max-w-xl justify-end">
                {allActiveSteps.map(({ step, remainingMs }) => {
                  const isReady = remainingMs <= 0;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => onSelectActiveStep?.(step)}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-all text-left cursor-pointer group shadow-2xs hover:shadow-xs active:scale-95 ${
                        isReady
                          ? 'bg-amber-50/90 border-amber-300 hover:bg-amber-100 hover:border-amber-400'
                          : 'bg-sky-50/80 border-sky-200 hover:bg-sky-100 hover:border-sky-300'
                      }`}
                      title={`Active step: ${step.mvTag} (${formatTimeRemaining(remainingMs)}). Click to jump to row in MV table`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                        isReady ? 'bg-amber-100 text-amber-600' : 'bg-sky-100 text-sky-600'
                      }`}>
                        <Clock className={`w-3.5 h-3.5 ${isReady ? 'text-amber-600 animate-pulse' : 'text-sky-600'}`} />
                      </div>
                      <div className="text-left">
                        <div className={`text-xs font-bold font-mono leading-tight ${
                          isReady ? 'text-amber-700 animate-pulse font-semibold' : 'text-slate-900 group-hover:text-sky-700'
                        }`}>
                          {formatTimeRemaining(remainingMs)}
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-mono text-slate-500 font-semibold truncate max-w-[120px]" title={step.mvTag}>
                          {step.mvTag}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="text-right">
              <div className="text-[11px] text-slate-500 uppercase tracking-wide">Valid Moves</div>
              <div className="text-xl sm:text-2xl font-bold font-mono text-slate-900">
                {stats.totalValidMoves} <span className="text-xs sm:text-sm font-normal text-slate-400">/ {stats.targetMovesTotal}</span>
              </div>
            </div>
            {/* Accurate Dynamic Circular Progress Gauge */}
            <div className="relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 56 56">
                {/* Background track */}
                <circle
                  cx="28"
                  cy="28"
                  r="23"
                  className="stroke-slate-100"
                  strokeWidth="4.5"
                  fill="none"
                />
                {/* Dynamic progress arc: starts at 12 o'clock and fills clockwise */}
                {progressPct > 0 && (
                  <circle
                    cx="28"
                    cy="28"
                    r="23"
                    className={`transition-all duration-700 ease-out ${
                      progressPct >= 100
                        ? 'stroke-emerald-600'
                        : progressPct >= 50
                        ? 'stroke-sky-600'
                        : 'stroke-sky-500'
                    }`}
                    strokeWidth="4.5"
                    strokeDasharray={144.513}
                    strokeDashoffset={144.513 - (Math.min(100, Math.max(0, progressPct)) / 100) * 144.513}
                    strokeLinecap="round"
                    fill="none"
                  />
                )}
              </svg>
              <span className="absolute font-bold text-xs sm:text-sm font-mono text-slate-800">
                {progressPct}%
              </span>
            </div>
          </div>
        </div>

        {/* Global Progress Bar */}
        <div className="mt-3.5">
          <div className="flex justify-between text-[11px] text-slate-500 mb-1.5 font-medium">
            <span>Overall Progress</span>
            <span>{stats.remainingMoves} moves remaining</span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className="bg-sky-600 h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Top Metric Cards - 5 Responsive Columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 text-xs mb-1">
            <span>Total MVs</span>
            <Layers className="w-3.5 h-3.5 text-slate-400" />
          </div>
          <div className="text-lg sm:text-xl font-bold text-slate-900 font-mono">{stats.totalMVs}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{subcontrollers.length} Subcontroller{subcontrollers.length === 1 ? '' : 's'}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 text-xs mb-1">
            <span>Valid Moves</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <div className="text-lg sm:text-xl font-bold text-emerald-700 font-mono">{stats.totalValidMoves}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{stats.remainingMoves} remaining</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 text-xs mb-1">
            <span>Model Ready</span>
            <TrendingUp className="w-3.5 h-3.5 text-sky-600" />
          </div>
          <div className="text-lg sm:text-xl font-bold text-sky-700 font-mono">{stats.overallModelReadinessPct}%</div>
          <div className="text-[10px] text-slate-400 mt-0.5">≥ {targetMovesPerMv} Valid Moves</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 text-xs mb-1">
            <span>Active Tests</span>
            <Clock className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-lg sm:text-xl font-bold text-amber-600 font-mono">{stats.activeTestsCount}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">In Progress</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
          <div className="flex items-center justify-between text-slate-500 text-xs mb-1">
            <span>Rejected</span>
            <XCircle className="w-3.5 h-3.5 text-rose-500" />
          </div>
          <div className="text-lg sm:text-xl font-bold text-rose-600 font-mono">{stats.rejectedTestsCount}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Upset/Disturbance</div>
        </div>
      </div>

      {/* Subcontrollers Readiness Breakdown */}
      {subcontrollers.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-slate-400" />
              <span>Subcontroller Breakdown</span>
            </h2>
            <span className="text-[11px] text-slate-400">Click to filter MVs</span>
          </div>

          <div className={`grid gap-3 w-full ${getSubcontrollerGridClass(subcontrollers.length)}`}>
            {subcontrollers.map((ctrl) => {
              const ctrlData = stats.controllerStats[ctrl];
              if (!ctrlData) return null;
              const isSelected = selectedController === ctrl;

            return (
              <div
                key={ctrl}
                onClick={() => onSelectController(isSelected ? 'ALL' : ctrl)}
                className={`cursor-pointer rounded-xl border p-4 transition-all flex flex-col justify-between ${
                  isSelected
                    ? 'bg-sky-50/50 border-sky-500 ring-2 ring-sky-500/20 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-600"></span>
                    <h3 className="font-semibold text-slate-900 text-sm">{ctrl}</h3>
                  </div>
                  <span className="text-xs font-mono font-semibold text-slate-700">
                    {ctrlData.readinessPct}%
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                  <span>{ctrlData.validMoves} / {ctrlData.targetMoves} moves</span>
                  <span>{ctrlData.totalMvs} MVs</span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-3">
                  <div
                    className="bg-sky-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${ctrlData.readinessPct}%` }}
                  ></div>
                </div>

                {/* Readiness badges counts */}
                <div className="grid grid-cols-3 gap-1.5 text-center text-[11px]">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-md px-1 py-1">
                    <div className="font-bold text-emerald-700 font-mono">{ctrlData.mvsReadyCount}</div>
                    <div className="text-[9px] text-emerald-600 font-medium">Ready ({targetMovesPerMv}+)</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-md px-1 py-1">
                    <div className="font-bold text-amber-700 font-mono">{ctrlData.mvsAdequateCount}</div>
                    <div className="text-[9px] text-amber-600 font-medium">Adequate</div>
                  </div>
                  <div className="bg-slate-100 border border-slate-200 rounded-md px-1 py-1">
                    <div className="font-bold text-slate-700 font-mono">{ctrlData.mvsInsufficientCount}</div>
                    <div className="text-[9px] text-slate-500 font-medium">Low (&lt;{Math.ceil(targetMovesPerMv * 0.5)})</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
};
